import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byText, type RegisterConfig } from "../register/registerLogic";

/**
 * The sick bay's dashboard: work waiting, today's visits, learners with
 * serious alerts, and the last fortnight. Mirrors apps/dashboard/sick_bay.py.
 * Names come only to someone who may read health records (`clinical`).
 */

interface Learner { learner: string; admission_number: string; class_name: string }
export interface TodayVisit extends Learner {
  id: number; time: string; visit_type: string; priority: string
  status: "scheduled" | "in_progress" | "completed" | "cancelled"; complaint: string; clinician: string
}
export interface WatchAlert extends Learner { id: number; alert_type: string; priority: string; title: string; instructions: string }
export interface SickBayDesk {
  generated_at: string; today: string; viewer_role: string; clinical: boolean
  work: Decision[]
  figures: { visits_today: number; seen_today: number; emergencies_today: number; on_course: number; with_allergies: number }
  today_visits: TodayVisit[] | null
  watch_list: WatchAlert[] | null
  fortnight: { days: { date: string; visits: number }[]; by_type: { visit_type: string; visits: number }[] }
}

type Tone = { label: string; dot: string };
export function visitTone(visit: Pick<TodayVisit, "status">): Tone {
  if (visit.status === "in_progress") return { label: "With the clinician", dot: "bg-sky-500" };
  if (visit.status === "completed") return { label: "Seen", dot: "bg-emerald-500" };
  return { label: "Waiting", dot: "bg-amber-500" };
}

/** Urgent first, then high; then the order they came in. */
const RANK: Record<string, number> = { urgent: 0, critical: 0, high: 1, medium: 2, low: 3 };
export const priorityRank = (priority: string) => RANK[priority] ?? 4;
export const priorityTone = (priority: string) =>
  priority === "urgent" || priority === "critical" ? "font-semibold text-red-700" : priority === "high" ? "font-semibold text-amber-800" : "text-gray-600";

export const visitConfig: RegisterConfig<TodayVisit> = {
  search: (row) => `${row.learner} ${row.admission_number} ${row.class_name} ${row.complaint} ${row.visit_type}`,
  views: [
    { key: "all", label: "All" },
    { key: "waiting", label: "Waiting", match: (row) => row.status === "scheduled" },
    { key: "with", label: "Being seen", match: (row) => row.status === "in_progress" },
    { key: "seen", label: "Seen", match: (row) => row.status === "completed" },
  ],
  sorts: [
    { key: "queue", label: "Queue (most urgent first)", compare: (a, b) => priorityRank(a.priority) - priorityRank(b.priority) || a.time.localeCompare(b.time) },
    { key: "time", label: "Time", compare: byText((row) => row.time) },
    { key: "learner", label: "Learner A–Z", compare: byText((row) => row.learner) },
  ],
};

export const watchConfig: RegisterConfig<WatchAlert> = {
  search: (row) => `${row.learner} ${row.admission_number} ${row.title} ${row.alert_type} ${row.instructions}`,
  views: [
    { key: "all", label: "All" },
    { key: "critical", label: "Critical", match: (row) => row.priority === "critical" },
    { key: "allergy", label: "Allergies", match: (row) => row.alert_type === "Allergy" },
  ],
  category: { label: "Class", value: (row) => row.class_name || "No class", inRowOrder: true },
  sorts: [
    { key: "class", label: "By class", compare: () => 0 },
    { key: "priority", label: "Most serious first", compare: (a, b) => priorityRank(a.priority) - priorityRank(b.priority) },
    { key: "learner", label: "Learner A–Z", compare: byText((row) => row.learner) },
  ],
};

export const viewerNote = (role: string) =>
  role === "school_doctor" ? "" : deskViewerNote(role, "school_doctor", "the sick bay's dashboard").replace(" The decisions listed are the ones your own account may take, and appraisals are those assigned to you.", " Everything here is the whole sick bay's.");
